import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import useAsyncEffect from 'use-async-effect';
import {
  getErrorNotification,
  useNotification,
} from '@components/Notification';
import { useConference } from '@core/dashboard/components/DashboardContent/Dolby';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useConferenceState } from '@hooks/conferenceContext';
import { useEventContext } from '@providers/EventProvider';
import { useMytaverse } from '@providers/MytaverseProvider';
import { PermissionName } from '@helpers/permissionsService';
import ZoomVideo, { CaptureVideoOption, ExceptionCode } from '@zoom/videosdk';
import ZoomService from '../services/ZoomService';
import { isZoomError, withTimeout } from '../helpers';
import {
  ICommunicationsContext,
  IZoomParticipantPositionData,
} from '@core/communications/providers/interfaces';
import { SpatialType } from '@interfaces/webSocketConnectionInfo';
import { default as zoomProviderLogger } from '../logger';

export const useZoom = (): ICommunicationsContext => {
  const zmClient = useMemo(() => {
    return ZoomVideo.createClient();
  }, []);

  // values from hooks
  const {
    currentParticipant,
    currentRegion,
    currentRoom,
    muted,
    setMuted,
    currentEvent,
    isGamecastCrash,
    shouldConnectToConference,
  } = useMytaverseEvent();
  const { ueWebcamScreenName, setVideoStarted, ueScreenScreenName } =
    useConference();
  const { activeSpeakerDeviceId, activeMicroDeviceId, activeCameraDeviceId } =
    useConferenceState();
  const { showNotification } = useNotification();
  const { t: translate } = useTranslation();
  const { isEventRun } = useEventContext();
  const { websocketSessionId } = useMytaverse();

  // states
  const [zoomConferenceName, setZoomConferenceName] = useState<string | null>(
    '',
  );
  const [isLimitedScreenResolution, setIsLimitedScreenResolution] =
    useState<boolean>(false);
  const [isCameraOn, setIsCameraOn] = useState<boolean>(false);
  const [isJoiningConference, setIsJoiningConference] =
    useState<boolean>(false);
  const [isSharingScreen, setIsSharingScreen] = useState<boolean>(false);
  const [isSharingScreenModalOpen, setIsSharingScreenModalOpen] =
    useState<boolean>(false);
  const [permissionsState, setPermissionsState] = useState({
    microphone: 'denied',
    camera: 'denied',
  });
  const [isCurrentParticipantSpeaking, setIsCurrentParticipantSpeaking] =
    useState<boolean>(false);
  const [forceRetryJoining, setForceRetryJoining] = useState<boolean>(false);
  const [isSharingToScreen, setIsSharingToScreen] = useState<boolean>(false);
  const [expandedScreenSharing, setExpandedScreenSharing] = useState(false);
  const [expandedCameraSharing, setExpandedCameraSharing] = useState(false);
  const [isInConference, setIsInConference] = useState<boolean>(false);
  const [user, setUser] = useState<any>(null);
  const [token, setToken] = useState<string | null>('');
  const [ue5Token, setUE5Token] = useState<string | null>('');
  const [mediaStream, setMediaStream] = useState<any>(null);
  const [micList, setMicList] = useState<Array<any>>([]);
  const [speakerList, setSpeakerList] = useState<Array<any>>([]);
  const [cameraList, setCameraList] = useState<Array<any>>([]);
  const [isAudioStarted, setIsAudioStarted] = useState<boolean>(false);

  // useRef
  const isJoiningConferenceRef = useRef(isJoiningConference);
  const speakingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Spatial Audio refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const spatialNodesRef = useRef<
    Map<
      string,
      {
        source: MediaElementAudioSourceNode | MediaStreamAudioSourceNode;
        stereoPanner: StereoPannerNode;
        gainNode: GainNode;
      }
    >
  >(new Map());
  const audioMonitorIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const isIOSChrome = useMemo(() => {
    try {
      const ua = navigator.userAgent || (navigator as any).vendor || '';
      const isIOS =
        /iPad|iPhone|iPod/i.test(ua) || /Macintosh;.*CPU iPhone OS/i.test(ua);
      const isCriOS = /CriOS/i.test(ua);
      return isIOS && isCriOS;
    } catch {
      return false;
    }
  }, []);

  const spatialAudioDisabled = isIOSChrome;
  const didWarmUpCameraRef = useRef(false);

  const alignPermissionsWithListDevices = useCallback(() => {
    const listCameras = mediaStream?.getCameraList?.() || [];
    const listSpeakers = mediaStream?.getSpeakerList?.() || [];
    const listMics = mediaStream?.getMicList?.() || [];

    setCameraList(permissionsState.camera === 'denied' ? [] : listCameras);
    setSpeakerList(
      permissionsState.microphone === 'denied' ? [] : listSpeakers,
    );
    setMicList(permissionsState.microphone === 'denied' ? [] : listMics);
  }, [permissionsState, mediaStream]);

  // useEffects
  useAsyncEffect(async () => {
    if (!zmClient || !currentRoom) {
      return;
    }

    zoomProviderLogger.log('Initializing Zoom client');

    try {
      // dirty fix
      // if current page is avatars selection disallow permissions check
      if (
        shouldConnectToConference &&
        window.location.href.indexOf('/my-avatars') === -1 &&
        window.location.href.indexOf('/avatars') === -1
      ) {
        checkPermissions();
      }
      await zmClient.init('en-US', 'Global', { patchJsMedia: true });
      alignPermissionsWithListDevices();

      const stream = zmClient.getMediaStream();
      setMediaStream(stream);
    } catch (e: any) {
      console.log(e.reason);
      zoomProviderLogger.log(e.reason);
    }

    return () => {
      zoomProviderLogger.log('Cleaning up Zoom client');
      ZoomVideo.destroyClient();
    };
  }, [zmClient, currentRoom]);

  useEffect(() => {
    if (
      mediaStream &&
      (permissionsState.microphone === 'granted' ||
        permissionsState.camera === 'granted')
    ) {
      alignPermissionsWithListDevices();
    }
  }, [mediaStream, permissionsState, alignPermissionsWithListDevices]);

  useAsyncEffect(async () => {
    const isInMeeting = !!zmClient.getSessionInfo()?.isInMeeting;
    const selectedCameraId = activeCameraDeviceId;
    const availableCameras = mediaStream?.getCameraList?.() || [];

    if (
      mediaStream &&
      isInMeeting &&
      selectedCameraId &&
      availableCameras.length &&
      availableCameras.some((d: any) => d.deviceId === selectedCameraId) &&
      mediaStream.getActiveCamera() !== selectedCameraId
    ) {
      try {
        await mediaStream.switchCamera(selectedCameraId);
      } catch (error) {
        zoomProviderLogger.log(`Switching camera error: ${error}`);
      }
    }
  }, [mediaStream, activeCameraDeviceId, cameraList, isInConference, zmClient]);

  useAsyncEffect(async () => {
    const isInMeeting = !!zmClient.getSessionInfo()?.isInMeeting;
    const selectedSpeakerId = activeSpeakerDeviceId;
    const availableSpeakers = mediaStream?.getSpeakerList?.() || [];

    if (
      mediaStream &&
      isInMeeting &&
      selectedSpeakerId &&
      availableSpeakers.length &&
      availableSpeakers.some((d: any) => d.deviceId === selectedSpeakerId) &&
      mediaStream.getActiveSpeaker() !== selectedSpeakerId
    ) {
      try {
        await mediaStream.switchSpeaker(selectedSpeakerId);
      } catch (error) {
        zoomProviderLogger.log(`Switching speaker error: ${error}`);
      }
    }
  }, [
    mediaStream,
    activeSpeakerDeviceId,
    speakerList,
    isInConference,
    zmClient,
  ]);

  useAsyncEffect(async () => {
    const isInMeeting = !!zmClient.getSessionInfo()?.isInMeeting;
    const selectedMicId = activeMicroDeviceId;
    const availableMics = mediaStream?.getMicList?.() || [];

    if (
      mediaStream &&
      isInMeeting &&
      selectedMicId &&
      availableMics.length &&
      availableMics.some((d: any) => d.deviceId === selectedMicId) &&
      mediaStream.getActiveMicrophone() !== selectedMicId
    ) {
      try {
        await mediaStream.switchMicrophone(selectedMicId);
      } catch (error) {
        zoomProviderLogger.log(`Switching microphone error: ${error}`);
      }
    }
  }, [mediaStream, activeMicroDeviceId, micList, isInConference, zmClient]);

  useAsyncEffect(async () => {
    if (
      !mediaStream ||
      !isEventRun ||
      !zmClient.getSessionInfo()?.isInMeeting ||
      isJoiningConference ||
      !isInConference ||
      isAudioStarted
    ) {
      return;
    }

    zoomProviderLogger.log('Starting audio stream');

    try {
      const audioStartOptions: any = {
        highBitrate: true,
        mute: muted,
      };
      const speakers = mediaStream.getSpeakerList?.() || [];
      if (
        activeSpeakerDeviceId &&
        speakers.some((d: any) => d.deviceId === activeSpeakerDeviceId)
      ) {
        audioStartOptions.speakerId = activeSpeakerDeviceId;
      }

      await mediaStream.startAudio(audioStartOptions);
      // On iOS Chrome, avoid muting original Zoom audio via spatial pipeline
      if (isIOSChrome) {
        setTimeout(() => {
          const audioElements = document.querySelectorAll('audio');
          Array.from(audioElements).forEach((audio) => {
            if (
              audio.id &&
              (audio.id.startsWith('ab-audio-') ||
                audio.id.startsWith('MainAudio-')) &&
              !audio.id.includes('-ue')
            ) {
              try {
                audio.muted = false;
                if ((audio as any)._spatialAudioOriginalState) {
                  delete (audio as any)._spatialAudioOriginalState;
                }
              } catch {
                /* no-op */
              }
            }
          });
        }, 0);
      }
      setIsAudioStarted(true);
      zoomProviderLogger.log('Successfully started audio stream');
    } catch (error) {
      setIsAudioStarted(false);
      zoomProviderLogger.error('Error starting audio stream:', error);
    }
  }, [
    mediaStream,
    activeSpeakerDeviceId,
    isEventRun,
    muted,
    isJoiningConference,
    isInConference,
    isAudioStarted,
  ]);

  useAsyncEffect(() => {
    if (isGamecastCrash) {
      zoomProviderLogger.log('Handling gamecast crash');
      ZoomVideo.destroyClient();
    }
  }, [isGamecastCrash, ZoomVideo]);

  useAsyncEffect(async () => {
    if (
      !mediaStream ||
      !currentRoom ||
      !zmClient.getSessionInfo()?.isInMeeting ||
      isJoiningConference ||
      !isInConference ||
      !isAudioStarted
    ) {
      return;
    }

    if (
      typeof mediaStream.muteAudio !== 'function' ||
      typeof mediaStream.unmuteAudio !== 'function'
    ) {
      zoomProviderLogger.log(
        'MediaStream not properly initialized for audio operations',
      );
      return;
    }

    try {
      if (muted) {
        await mediaStream.muteAudio();
        return;
      }

      await mediaStream.unmuteAudio();
    } catch (error) {
      zoomProviderLogger.error('Error in the muting/unmuting: ', error);
    }
  }, [
    muted,
    mediaStream,
    currentRoom,
    isJoiningConference,
    isInConference,
    isAudioStarted,
  ]);

  // helpers
  const handleZoomError = (error: any) => {
    if (isZoomError(error)) {
      switch (error.errorCode) {
        case ExceptionCode.VIDEO_CAMERA_PERMISSION_DENIED:
          showNotification(
            getErrorNotification({
              message: translate(
                'notifications.video.start.userForbiddenCapture',
              ),
            }),
          );
          break;
        case ExceptionCode.VIDEO_CAMERA_TAKEN:
          showNotification(
            getErrorNotification({
              message: translate('notifications.video.start.cameraIsTaken'),
            }),
          );
          break;
      }
    }
  };

  // Functionalities: Screen Sharing
  const startScreenSharing = useCallback(async () => {
    try {
      zoomProviderLogger.log('startScreenSharing called');
      if (
        !mediaStream ||
        !ueScreenScreenName ||
        !currentParticipant ||
        !currentEvent ||
        !currentRoom ||
        !zoomConferenceName
      ) {
        return;
      }
      const currentZoomUser = zmClient.getCurrentUserInfo();
      setIsSharingScreenModalOpen(true);

      if (mediaStream.isStartShareScreenWithVideoElement()) {
        const video = document.getElementById(
          'screen-share-content-video',
        ) as HTMLVideoElement | null;

        if (!video) {
          return;
        }

        await mediaStream.startShareScreen(video);

        //reformat in case of preview
        // video.style.display = 'none';
      } else {
        const canvas = document.getElementById(
          'screen-share-content-canvas',
        ) as HTMLCanvasElement | null;

        if (!canvas) {
          return;
        }

        await mediaStream.startShareScreen(
          canvas,
          isLimitedScreenResolution
            ? {
                captureHeight: 1080,
                captureWidth: 1920,
                optimizedForSharedVideo: true,
              }
            : undefined,
        );

        canvas.style.display = 'none';
      }

      await ZoomService.createZoomMediaStream({
        mediaStreamId: `${currentParticipant.participantId}-screen`,
        ueScreenName: ueScreenScreenName,
        owner: {
          id: currentParticipant?.participantId,
          name: currentParticipant?.name,
        },
        source: 'SCREEN',
        muted: true,
        eventId: currentEvent.eventId,
        roomId: currentRoom.roomId,
        participantId: currentParticipant.participantId,
        region: currentRegion ? currentRegion.region : '',
        zoomUserId: currentZoomUser.userId,
        conferenceId: zoomConferenceName,
        websocketSessionId,
      });

      setIsSharingScreen(true);
    } catch (error) {
      if (isZoomError(error)) {
        if (error.errorCode === ExceptionCode.SCREEN_SHARE_USER_DENIED) {
          // we ignore this error because user just clicked Cancel button on the screen share dialog
          zoomProviderLogger.log('Screen share user denied');
          return;
        }
      }

      throw error;
    } finally {
      setIsSharingScreenModalOpen(false);
    }
  }, [
    mediaStream,
    ueScreenScreenName,
    currentParticipant,
    currentEvent,
    zmClient,
    currentRoom,
    currentRegion,
    zoomConferenceName,
    isLimitedScreenResolution,
    websocketSessionId,
  ]);

  const stopScreenSharing = useCallback(async () => {
    zoomProviderLogger.log('stopScreenSharing called');
    if (!currentParticipant) {
      return;
    }

    if (!mediaStream || !zmClient.getSessionInfo()?.isInMeeting) {
      return;
    }

    try {
      await mediaStream?.stopShareScreen();
      await ZoomService.deleteZoomMediaStream({
        mediaStreamId: `${currentParticipant.participantId}-screen`,
      });
    } catch (error) {
      zoomProviderLogger.log(`Error in the stopScreenSharing: ${error}`);
    }
    setIsSharingScreen(false);
  }, [mediaStream, ueScreenScreenName, zmClient]);

  // Functionality: Share camera to screen
  const startShareCameraToScreen = useCallback(async () => {
    zoomProviderLogger.log('startShareCameraToScreen called');
    try {
      const currentZoomUser = zmClient.getCurrentUserInfo();

      if (
        !currentZoomUser?.bVideoShare &&
        mediaStream &&
        currentEvent &&
        currentRoom &&
        currentParticipant &&
        ueWebcamScreenName &&
        zoomConferenceName
      ) {
        await mediaStream.startVideo({
          cameraId: activeCameraDeviceId,
        });

        await ZoomService.createZoomMediaStream({
          mediaStreamId: `${currentParticipant.participantId}-camera`,
          ueScreenName: ueWebcamScreenName,
          owner: {
            id: currentParticipant?.participantId,
            name: currentParticipant?.name,
          },
          source: 'CAMERA',
          muted: true,
          eventId: currentEvent.eventId,
          roomId: currentRoom.roomId,
          participantId: currentParticipant.participantId,
          region: currentRegion ? currentRegion.region : '',
          zoomUserId: currentZoomUser.userId,
          conferenceId: zoomConferenceName,
          websocketSessionId,
        });

        setIsSharingToScreen(true);
        setVideoStarted(true);
        const currentUser = zmClient.getCurrentUserInfo();
        if (currentUser) {
          setUser(currentUser);
        }
      }
    } catch (error: unknown) {
      setVideoStarted(false);
      setIsSharingToScreen(false);
      handleZoomError(error);
    }
  }, [
    activeCameraDeviceId,
    mediaStream,
    currentRoom,
    currentRegion,
    currentEvent,
    ueWebcamScreenName,
    zoomConferenceName,
    currentParticipant,
    zmClient,
    websocketSessionId,
  ]);

  const stopShareCameraToScreen = useCallback(async () => {
    if (!mediaStream || !currentParticipant) {
      return;
    }

    const mediaStreamId = mediaStream.getActiveVideoId();

    if (mediaStreamId) {
      await Promise.all([
        mediaStream.stopVideo(),
        ZoomService.deleteZoomMediaStream({
          mediaStreamId: `${currentParticipant.participantId}-camera`,
        }),
      ]);
      setIsSharingToScreen(false);
      setVideoStarted(false);
      const currentUser = zmClient.getCurrentUserInfo();
      if (currentUser) {
        setUser(currentUser);
      }
    }
  }, [mediaStream, currentParticipant]);

  // Functionalities: Video Sharing
  const startVideoSharing = useCallback(
    async (videoRef?: MutableRefObject<HTMLVideoElement>) => {
      if (!mediaStream) {
        return;
      }
      const startVideoOptions: CaptureVideoOption = {
        videoElement: videoRef?.current,
        cameraId: activeCameraDeviceId,
      };

      if (mediaStream?.isSupportVirtualBackground()) {
        Object.assign(startVideoOptions, {
          virtualBackground: { imageUrl: 'blur' },
        });
      }

      try {
        await mediaStream.startVideo(startVideoOptions);
        setIsCameraOn(true);
      } catch (error: unknown) {
        setIsCameraOn(false);
        handleZoomError(error);
      }
    },
    [mediaStream, activeCameraDeviceId],
  );

  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        zoomProviderLogger.log('Audio context initialized for spatial audio');
      } catch (error) {
        zoomProviderLogger.error('Failed to initialize audio context:', error);
      }
    }
    return audioContextRef.current;
  }, []);

  const resumeAudioContextIfNeeded = useCallback(async () => {
    if (
      audioContextRef.current &&
      audioContextRef.current.state === 'suspended'
    ) {
      try {
        await audioContextRef.current.resume();
        zoomProviderLogger.log('Audio context resumed');
      } catch (error) {
        zoomProviderLogger.error('Failed to resume audio context:', error);
      }
    }
  }, []);

  const ensureAudioContextRunning = useCallback(async (): Promise<boolean> => {
    const ctx = audioContextRef.current;
    if (!ctx) {
      return false;
    }
    const isRunning =
      ctx.state !== 'suspended' &&
      ctx.state !== 'closed' &&
      (ctx.state as any) !== 'interrupted';
    if (isRunning) {
      return true;
    }
    try {
      await ctx.resume();
      const resumedRunning =
        ctx.state !== 'suspended' &&
        ctx.state !== 'closed' &&
        (ctx.state as any) !== 'interrupted';
      return resumedRunning;
    } catch (error) {
      // iOS/WebKit requires a user gesture to resume. Attach one-off listeners.
      const attemptResume = () => {
        audioContextRef.current?.resume().catch(() => {});
      };
      document.addEventListener('click', attemptResume, { once: true });
      document.addEventListener('touchend', attemptResume, { once: true });
      return false;
    }
  }, []);

  const createSpatialAudioNode = useCallback(
    (audioElement: HTMLAudioElement, userId: string) => {
      const audioContext = initAudioContext();
      if (!audioContext) {
        return;
      }

      if (
        audioContextRef.current &&
        (audioContextRef.current.state === 'suspended' ||
          audioContextRef.current.state === 'closed' ||
          (audioContextRef.current.state as any) === 'interrupted')
      ) {
        // Avoid creating nodes until context is running to prevent silent paths on iOS
        return;
      }

      if (spatialNodesRef.current.has(userId)) {
        return;
      }

      try {
        if (audioElement.readyState < 2) {
          // Audio element not ready yet
        }

        const source = audioContext.createMediaElementSource(audioElement);

        const stereoPanner = audioContext.createStereoPanner();

        const gainNode = audioContext.createGain();

        stereoPanner.pan.value = 0;
        gainNode.gain.value = 1;

        source.connect(stereoPanner);
        stereoPanner.connect(gainNode);
        gainNode.connect(audioContext.destination);

        spatialNodesRef.current.set(userId, {
          source,
          stereoPanner,
          gainNode,
        });

        const testPanValue = 0.5;
        stereoPanner.pan.setValueAtTime(testPanValue, audioContext.currentTime);

        setTimeout(() => {
          if (spatialNodesRef.current.has(userId)) {
            stereoPanner.pan.setValueAtTime(0, audioContext.currentTime);
          }
        }, 500);
      } catch (error) {
        zoomProviderLogger.error('Error creating spatial audio node:', error);
      }
    },
    [initAudioContext],
  );

  const applySpatialAudioEffect = useCallback(
    (participant: IZoomParticipantPositionData) => {
      const userId = participant.zoomUserId;
      const spatialNode = spatialNodesRef.current.get(userId);

      if (!spatialNode || !audioContextRef.current) {
        return;
      }

      try {
        const angle = parseFloat(participant.angle);
        const distance = parseFloat(participant.distance);

        if (isNaN(angle) || isNaN(distance)) {
          return;
        }

        const maxDistance = 1000;
        const minDistance = 0;
        const minVolume = 0.0;
        const maxVolume = 1.0;

        const limitedDistance = Math.min(distance, maxDistance);

        let volume =
          maxVolume -
          ((limitedDistance - minDistance) / (maxDistance - minDistance)) *
            (maxVolume - minVolume);
        volume = Math.max(minVolume, Math.min(maxVolume, volume));

        if (distance > maxDistance) {
          volume = minVolume;
        }

        let normalizedAngle = angle;
        if (normalizedAngle > 180) {
          normalizedAngle -= 360;
        }

        const basePanValue = normalizedAngle / 180;

        const closeDistanceThreshold = 300;
        const farDistanceThreshold = 800;

        let panMultiplier = 1.0;
        if (distance <= closeDistanceThreshold) {
          panMultiplier = 1.0 + 0.4 * (1 - distance / closeDistanceThreshold);
        } else if (distance <= farDistanceThreshold) {
          const distanceRatio =
            (distance - closeDistanceThreshold) /
            (farDistanceThreshold - closeDistanceThreshold);
          panMultiplier = 1.0 - 0.3 * distanceRatio;
        } else {
          panMultiplier = 0.7;
        }

        const maxPan = 1.0;
        const enhancedPanValue = basePanValue * panMultiplier * maxPan;
        const panValue = Math.max(-maxPan, Math.min(maxPan, enhancedPanValue));

        const currentTime = audioContextRef.current.currentTime;
        const transitionDuration = 0.1;

        spatialNode.stereoPanner.pan.setValueAtTime(
          spatialNode.stereoPanner.pan.value,
          currentTime,
        );
        spatialNode.stereoPanner.pan.linearRampToValueAtTime(
          panValue,
          currentTime + transitionDuration,
        );

        spatialNode.gainNode.gain.setValueAtTime(
          spatialNode.gainNode.gain.value,
          currentTime,
        );
        spatialNode.gainNode.gain.linearRampToValueAtTime(
          volume,
          currentTime + transitionDuration,
        );
      } catch (error) {
        zoomProviderLogger.error('Error applying spatial audio effect:', error);
      }
    },
    [],
  );

  const getParticipantMediaStream = useCallback(
    (userId: string) => {
      try {
        const allParticipants = zmClient.getAllUser().filter((participant) => {
          const displayName = participant.displayName?.toLowerCase() || '';
          return (
            !displayName.includes('-ue') &&
            !displayName.includes('ue') &&
            displayName !== ''
          );
        });

        const userIdNum = parseFloat(userId);
        const targetParticipant = allParticipants.find((p) => {
          const participantId = p.userId;
          return (
            participantId === userIdNum ||
            participantId === userIdNum + 1 ||
            participantId === userIdNum - 1 ||
            participantId.toString() === userId
          );
        });

        if (targetParticipant && mediaStream) {
          let audioTrack = null;

          if (typeof mediaStream.getRemoteAudioTrack === 'function') {
            try {
              audioTrack = mediaStream.getRemoteAudioTrack(
                targetParticipant.userId,
              );
              if (audioTrack) {
                return audioTrack;
              }
            } catch (error) {
              zoomProviderLogger.error(
                'Error getting remote audio track:',
                error,
              );
            }
          }

          if (typeof mediaStream.getRemoteAudioStream === 'function') {
            try {
              const audioStream = mediaStream.getRemoteAudioStream(
                targetParticipant.userId,
              );
              if (audioStream && audioStream.getTracks) {
                const tracks = audioStream.getTracks();
                if (tracks.length > 0) {
                  return audioStream;
                }
              }
            } catch (error) {
              zoomProviderLogger.error(
                'Error getting remote audio stream:',
                error,
              );
            }
          }
        }

        return null;
      } catch (error) {
        zoomProviderLogger.error(
          'Error getting participant media stream:',
          error,
        );
        return null;
      }
    },
    [zmClient, mediaStream],
  );

  const createSpatialAudioNodeFromStream = useCallback(
    (mediaStreamTrack: any, userId: string) => {
      const audioContext = initAudioContext();
      if (!audioContext || spatialNodesRef.current.has(userId)) {
        return;
      }

      if (
        audioContextRef.current &&
        (audioContextRef.current.state === 'suspended' ||
          audioContextRef.current.state === 'closed' ||
          (audioContextRef.current.state as any) === 'interrupted')
      ) {
        // Avoid creating nodes until context is running to prevent silent paths on iOS
        return;
      }

      try {
        const mediaStream = new MediaStream([mediaStreamTrack]);
        const source = audioContext.createMediaStreamSource(mediaStream);
        const stereoPanner = audioContext.createStereoPanner();
        const gainNode = audioContext.createGain();

        stereoPanner.pan.value = 0;
        gainNode.gain.value = 1;

        source.connect(stereoPanner);
        stereoPanner.connect(gainNode);
        gainNode.connect(audioContext.destination);

        spatialNodesRef.current.set(userId, {
          source,
          stereoPanner,
          gainNode,
        });
      } catch (error) {
        zoomProviderLogger.error(
          'Error creating spatial audio node from stream:',
          error,
        );
      }
    },
    [initAudioContext],
  );

  const resetSpatialAudioSettings = useCallback(() => {
    spatialNodesRef.current.forEach((node) => {
      try {
        node.source.disconnect();
        node.stereoPanner.disconnect();
        node.gainNode.disconnect();
      } catch (error) {
        zoomProviderLogger.error(
          'Error disconnecting spatial audio node:',
          error,
        );
      }
    });
    spatialNodesRef.current.clear();

    if (audioMonitorIntervalRef.current) {
      clearInterval(audioMonitorIntervalRef.current);
      audioMonitorIntervalRef.current = null;
    }

    const audioElements = document.querySelectorAll('audio');
    Array.from(audioElements).forEach((audio) => {
      const originalState = (audio as any)._spatialAudioOriginalState;
      if (originalState) {
        try {
          audio.volume = originalState.volume;
          audio.muted = originalState.muted;
          delete (audio as any)._spatialAudioOriginalState;
        } catch (error) {
          zoomProviderLogger.error(
            'Error resetting original state for audio element:',
            error,
          );
          audio.volume = 1.0;
          audio.muted = false;
        }
      } else if (audio.volume === 0) {
        try {
          audio.volume = 1.0;
          audio.muted = false;
        } catch (error) {
          zoomProviderLogger.error(
            'Error resetting volume for audio element:',
            error,
          );
        }
      }
    });
  }, []);

  // Fallback: Find and setup audio elements for spatial audio
  const setupSpatialAudioForParticipants = useCallback(
    async (participants: IZoomParticipantPositionData[]) => {
      if (spatialAudioDisabled) {
        // Skip spatial setup entirely on iOS Chrome
        return;
      }
      await resumeAudioContextIfNeeded();
      const contextRunning = await ensureAudioContextRunning();

      participants.forEach(async (participant) => {
        const userId = participant.zoomUserId;
        if (!userId || userId === '') {
          return;
        }

        if (spatialNodesRef.current.has(userId)) {
          applySpatialAudioEffect(participant);
          return;
        }

        const mediaStreamTrack = getParticipantMediaStream(userId);
        if (mediaStreamTrack) {
          createSpatialAudioNodeFromStream(mediaStreamTrack, userId);
          setTimeout(() => {
            if (spatialNodesRef.current.has(userId)) {
              const audioElements = document.querySelectorAll('audio');
              Array.from(audioElements).forEach((audio) => {
                if (
                  audio.id &&
                  (audio.id.startsWith('ab-audio-') ||
                    audio.id.startsWith('MainAudio-')) &&
                  !audio.id.includes('-ue') &&
                  audio.id !== 'gamecastAudioTag'
                ) {
                  if (contextRunning) {
                    if (!(audio as any)._spatialAudioOriginalState) {
                      (audio as any)._spatialAudioOriginalState = {
                        volume: audio.volume,
                        muted: audio.muted,
                      };
                    }
                    audio.volume = 0;
                    audio.muted = true;
                  }
                }
              });
              applySpatialAudioEffect(participant);
            }
          }, 100);
          return;
        }

        const audioElements = document.querySelectorAll('audio');

        // Do not pre-mute here. We'll mute only after a spatial node is created.

        const participantAudioElement = Array.from(audioElements).find(
          (audio) => {
            const audioId = audio.id;
            const userIdNum = parseFloat(userId);

            if (
              !audioId ||
              audioId.includes('-ue') ||
              audioId.toLowerCase().includes('ue')
            ) {
              return false;
            }

            return (
              (audioId.startsWith('ab-audio-') ||
                audioId.startsWith('MainAudio-')) &&
              (audioId.includes(userId) ||
                audioId.includes(userIdNum.toString()) ||
                audioId.includes((userIdNum + 1).toString()) ||
                audioId.includes((userIdNum - 1).toString()))
            );
          },
        );

        if (participantAudioElement) {
          const setupSpatialAudioForElement = (
            audioElement: HTMLAudioElement,
          ) => {
            if (typeof (audioElement as any).captureStream === 'function') {
              try {
                const capturedStream = (audioElement as any).captureStream();
                if (
                  capturedStream &&
                  capturedStream.getAudioTracks().length > 0
                ) {
                  createSpatialAudioNodeFromStream(
                    capturedStream.getAudioTracks()[0],
                    userId,
                  );
                  setTimeout(() => {
                    if (spatialNodesRef.current.has(userId)) {
                      if (contextRunning) {
                        if (!(audioElement as any)._spatialAudioOriginalState) {
                          (audioElement as any)._spatialAudioOriginalState = {
                            volume: audioElement.volume,
                            muted: audioElement.muted,
                          };
                        }
                        audioElement.volume = 0;
                        audioElement.muted = true;
                      }
                      applySpatialAudioEffect(participant);
                    }
                  }, 100);
                  return;
                }
              } catch (error) {
                zoomProviderLogger.error(
                  'Error capturing stream for spatial audio:',
                  error,
                );
              }
            }

            if (typeof (audioElement as any).mozCaptureStream === 'function') {
              try {
                const capturedStream = (audioElement as any).mozCaptureStream();
                if (
                  capturedStream &&
                  capturedStream.getAudioTracks().length > 0
                ) {
                  createSpatialAudioNodeFromStream(
                    capturedStream.getAudioTracks()[0],
                    userId,
                  );
                  setTimeout(() => {
                    if (spatialNodesRef.current.has(userId)) {
                      if (contextRunning) {
                        if (!(audioElement as any)._spatialAudioOriginalState) {
                          (audioElement as any)._spatialAudioOriginalState = {
                            volume: audioElement.volume,
                            muted: audioElement.muted,
                          };
                        }
                        audioElement.volume = 0;
                        audioElement.muted = true;
                      }
                      applySpatialAudioEffect(participant);
                    }
                  }, 100);
                  return;
                }
              } catch (error) {
                zoomProviderLogger.error(
                  'Error capturing stream for spatial audio:',
                  error,
                );
              }
            }

            createSpatialAudioNode(audioElement, userId);
            setTimeout(() => {
              if (spatialNodesRef.current.has(userId)) {
                if (contextRunning) {
                  if (!(audioElement as any)._spatialAudioOriginalState) {
                    (audioElement as any)._spatialAudioOriginalState = {
                      volume: audioElement.volume,
                      muted: audioElement.muted,
                    };
                  }
                  // Mute only after node exists to avoid premature silence
                  audioElement.volume = 0;
                  audioElement.muted = true;
                }
                applySpatialAudioEffect(participant);
              }
            }, 100);
          };

          if (participantAudioElement.readyState >= 2) {
            setupSpatialAudioForElement(
              participantAudioElement as HTMLAudioElement,
            );
          } else {
            const onCanPlay = () => {
              setupSpatialAudioForElement(
                participantAudioElement as HTMLAudioElement,
              );
              participantAudioElement.removeEventListener('canplay', onCanPlay);
              participantAudioElement.removeEventListener(
                'loadeddata',
                onCanPlay,
              );
            };

            participantAudioElement.addEventListener('canplay', onCanPlay);
            participantAudioElement.addEventListener('loadeddata', onCanPlay);

            setTimeout(() => {
              if (participantAudioElement.readyState >= 2) {
                setupSpatialAudioForElement(
                  participantAudioElement as HTMLAudioElement,
                );
                participantAudioElement.removeEventListener(
                  'canplay',
                  onCanPlay,
                );
                participantAudioElement.removeEventListener(
                  'loadeddata',
                  onCanPlay,
                );
              } else {
                participantAudioElement.removeEventListener(
                  'canplay',
                  onCanPlay,
                );
                participantAudioElement.removeEventListener(
                  'loadeddata',
                  onCanPlay,
                );
              }
            }, 5000);
          }
        }
      });
    },
    [
      applySpatialAudioEffect,
      resumeAudioContextIfNeeded,
      getParticipantMediaStream,
      createSpatialAudioNodeFromStream,
      createSpatialAudioNode,
    ],
  );

  const changeParticipantsVolumeLevel = (
    participants: IZoomParticipantPositionData[],
    regionSpatialType?: SpatialType,
  ) => {
    if (!mediaStream || !zoomConferenceName) {
      return;
    }

    const sessionInfo = zmClient.getSessionInfo();
    if (!sessionInfo?.isInMeeting || !isInConference) {
      if (audioMonitorIntervalRef.current) {
        clearInterval(audioMonitorIntervalRef.current);
        audioMonitorIntervalRef.current = null;
      }
      return;
    }

    const currentZoomId = sessionInfo.userId;

    participants.forEach((participant) => {
      if (!participant.zoomUserId || participant.zoomUserId === '') {
        return;
      }

      const zoomUserId = parseFloat(participant.zoomUserId);

      if (isNaN(zoomUserId) || zoomUserId === currentZoomId) {
        return;
      }

      if (regionSpatialType === SpatialType.OneToMany) {
        mediaStream.adjustUserAudioVolumeLocally(zoomUserId, 100);
        return;
      } else if (
        regionSpatialType === SpatialType.DistanceAudio ||
        !regionSpatialType
      ) {
        const distance = parseFloat(participant.distance || '0');
        const maxDistance = 1000;

        const volume = Math.max(0, Math.min(1, 1 - distance / maxDistance));
        mediaStream.adjustUserAudioVolumeLocally(
          zoomUserId,
          Math.round(volume * 100),
        );
        return;
      }

      // For SPATIAL_AUDIO, SPATIAL_MEETING, or other spatial types
      // Mute only if we already created a spatial node AND audio context is running; otherwise fallback to distance volume
      const hasSpatialNode = spatialNodesRef.current.has(
        participant.zoomUserId,
      );
      const audioContextRunning =
        !!audioContextRef.current &&
        audioContextRef.current.state !== 'suspended' &&
        audioContextRef.current.state !== 'closed' &&
        (audioContextRef.current.state as any) !== 'interrupted';
      if (spatialAudioDisabled || !(hasSpatialNode && audioContextRunning)) {
        const distance = parseFloat(participant.distance || '0');
        const maxDistance = 1000;
        const volume = Math.max(0, Math.min(1, 1 - distance / maxDistance));
        mediaStream.adjustUserAudioVolumeLocally(
          zoomUserId,
          Math.round(volume * 100),
        );
      } else {
        mediaStream.adjustUserAudioVolumeLocally(zoomUserId, 0);
      }
    });

    // Only apply spatial audio effects for spatial region types
    if (
      !spatialAudioDisabled &&
      regionSpatialType &&
      (regionSpatialType === SpatialType.SpatialAudio ||
        regionSpatialType === SpatialType.SpatialMeeting ||
        regionSpatialType === SpatialType.None) &&
      sessionInfo.isInMeeting &&
      isInConference
    ) {
      try {
        setupSpatialAudioForParticipants(participants)
          .then(() => {
            if (!audioMonitorIntervalRef.current) {
              startAudioMonitoring();
            }
          })
          .catch(() => {});
      } catch (error) {
        zoomProviderLogger.error(
          'Error setting up spatial audio for participants:',
          error,
        );
      }
    }
  };

  // Functionalities: Join conference
  const handleJoinToConference = useCallback(
    async (eventId: string, roomId: string, regionName?: string) => {
      const sessionName = regionName
        ? `${eventId}-${roomId}-${regionName}`
        : `${eventId}-${roomId}`;

      let webToken, ue5Token, currentSession;

      try {
        alignPermissionsWithListDevices();
      } catch (error) {
        zoomProviderLogger.error('Error aligning permissions:', error);
      }

      try {
        const currentSessionFromZoom = zmClient.getSessionInfo()?.topic;
        currentSession = currentSessionFromZoom;

        if (
          !currentParticipant ||
          (!forceRetryJoining &&
            sessionName?.toLowerCase() === currentSession?.toLowerCase())
        ) {
          return;
        }

        setIsJoiningConference(true);
        // can we remove this and hope for the useEffect to set the value properly and fast enough?
        isJoiningConferenceRef.current = true;

        if (isInConference) {
          try {
            if (isSharingScreen) {
              await stopScreenSharing();
            }

            if (isSharingToScreen) {
              await stopShareCameraToScreen();
            }

            resetSpatialAudioSettings();

            await zmClient.leave();
            setIsInConference(false);
            setIsAudioStarted(false);
          } catch (error) {
            zoomProviderLogger.error(
              'Error leaving previous conference:',
              error,
            );
            setIsAudioStarted(false);
          }
        }

        const [webTokenZoom, ue5TokenZoom] = await Promise.all([
          ZoomService.generateZoomToken({
            role: 1,
            expirationSeconds: 10800,
            sessionName: sessionName,
            userIdentity: currentParticipant.streamChatMemberId,
          }),
          ZoomService.generateZoomToken({
            role: 1,
            expirationSeconds: 10800,
            sessionName: sessionName,
            userIdentity: currentParticipant.streamChatMemberId,
          }),
        ]);
        webToken = webTokenZoom;
        ue5Token = ue5TokenZoom;
      } catch (error) {
        zoomProviderLogger.error(error);
        setIsInConference(false);
        setZoomConferenceName(null);
        setIsJoiningConference(false);
        setIsAudioStarted(false);
        // can we remove this and hope for the useEffect to set the value properly and fast enough?
        isJoiningConferenceRef.current = false;
        setForceRetryJoining(true);
        return;
      }

      try {
        await withTimeout(
          zmClient.join(
            sessionName,
            webToken.signature,
            currentParticipant.fullName ||
              currentParticipant.email ||
              currentParticipant.participantId,
          ),
          10000,
          'zmClient.join has timed out',
        );
      } catch (error) {
        zoomProviderLogger.error(error);
        setIsInConference(false);
        setZoomConferenceName(null);
        setIsJoiningConference(false);
        setIsAudioStarted(false);
        // can we remove this and hope for the useEffect to set the value properly and fast enough?
        isJoiningConferenceRef.current = false;
        if (!forceRetryJoining) {
          // first time erroring
          showNotification(
            getErrorNotification({
              message: translate('notifications.zoom.join.timeout'),
            }),
          );
        } else {
          // second or more time erroring
          showNotification(
            getErrorNotification({
              message: translate('notifications.zoom.join.failed'),
              withReloadButton: true,
            }),
          );
        }
        setForceRetryJoining(true);
        zmClient.leave();
        return;
      }

      zoomProviderLogger.log(`Current Zoom Session: ${sessionName}`);

      setForceRetryJoining(false);
      setIsInConference(true);
      const currentZoomUser = zmClient.getCurrentUserInfo();
      if (currentZoomUser) {
        setUser(currentZoomUser);
      }
      setZoomConferenceName(sessionName);
      if (webToken) {
        setToken(webToken.signature);
      }
      if (ue5Token) {
        setUE5Token(ue5Token.signature);
      }
      setIsJoiningConference(false);
      // can we remove this and hope for the useEffect to set the value properly and fast enough?
      isJoiningConferenceRef.current = false;

      try {
        if (isCameraOn) {
          await startVideoSharing();
        }
      } catch (error) {
        zoomProviderLogger.error('error with the camera ', error);
      }
    },
    [
      zmClient,
      mediaStream,
      currentParticipant,
      zoomConferenceName,
      isSharingScreen,
      isSharingToScreen,
      isInConference,
      stopShareCameraToScreen,
      stopScreenSharing,
      resetSpatialAudioSettings,
      isCameraOn,
      forceRetryJoining,
      startVideoSharing,
    ],
  );

  // Functionalities: zmClient on actions - Passively Stop Sharing
  const handlePassivelyStopShare = useCallback(async () => {
    await stopScreenSharing();
  }, [stopScreenSharing]);

  // Functionalities: zmClient on actions - Active Change speaker
  const onActiveSpeakerChange = useCallback(
    (payload: any) => {
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
        speakingTimeoutRef.current = null;
      }

      if (Array.isArray(payload) && payload.length > 0) {
        const isCurrentUserSpeaking = payload.some(
          (item: { userId: number; displayName: string }) =>
            item.userId === zmClient.getSessionInfo().userId,
        );

        setIsCurrentParticipantSpeaking(isCurrentUserSpeaking);

        if (isCurrentUserSpeaking) {
          speakingTimeoutRef.current = setTimeout(() => {
            setIsCurrentParticipantSpeaking(false);
            speakingTimeoutRef.current = null;
          }, 5000);
        }

        return;
      }

      setIsCurrentParticipantSpeaking(false);
    },
    [zmClient],
  );

  const checkSinglePermission = async (
    name: PermissionName,
  ): Promise<'granted' | 'denied' | 'prompt'> => {
    try {
      const anyNavigator: any = navigator as any;
      if (anyNavigator?.permissions?.query) {
        const status = await anyNavigator.permissions.query({
          name: name as any,
        });
        return status.state as 'granted' | 'denied' | 'prompt';
      }
    } catch (_error) {
      /* no-op: permissions API not available or failed */
    }

    try {
      const constraints =
        name === 'microphone'
          ? ({ audio: true } as any)
          : ({ video: true } as any);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      try {
        stream.getTracks().forEach((t) => t.stop());
      } catch {
        /* no-op: best effort to stop tracks */
      }
      return 'granted';
    } catch (err: any) {
      const message = String(err?.name || err?.message || err || '');
      if (/denied|NotAllowedError|Permission/i.test(message)) {
        return 'denied';
      }
      return 'prompt';
    }
  };

  const handlePermissionChange = useCallback(async () => {
    if (mediaStream) {
      const microphonePermissionState = await checkSinglePermission(
        'microphone',
      );
      const cameraPermissionState = await checkSinglePermission('camera');

      setPermissionsState({
        microphone: microphonePermissionState,
        camera: cameraPermissionState,
      });

      if (!isJoiningConferenceRef.current) {
        setForceRetryJoining(true);
      }
    }
  }, [mediaStream]);

  // Functionalities: zmClient on actions
  useEffect(() => {
    zmClient.on('passively-stop-share', handlePassivelyStopShare);
    zmClient.on('device-permission-change', handlePermissionChange);
    zmClient.on('active-speaker', onActiveSpeakerChange);

    return () => {
      zmClient.off('passively-stop-share', handlePassivelyStopShare);
      zmClient.off('device-permission-change', handlePermissionChange);
      zmClient.off('active-speaker', onActiveSpeakerChange);
    };
  }, [zmClient, handlePassivelyStopShare, onActiveSpeakerChange]);

  // Functionalities: toggle video sharing
  const handleToggleVideoSharing = useCallback(
    async (videoRef?: MutableRefObject<HTMLVideoElement>) => {
      if (!mediaStream) {
        return;
      }

      if (isCameraOn) {
        await mediaStream.stopVideo();
        setIsCameraOn(false);
        return;
      }

      await startVideoSharing(videoRef);
    },
    [isCameraOn, mediaStream, startVideoSharing],
  );

  const requestPermission = async (
    name: 'microphone' | 'camera',
  ): Promise<'granted' | 'denied'> => {
    zoomProviderLogger.log(`requestPermission called for: ${name}`);
    try {
      const constraints =
        name === 'microphone'
          ? ({ audio: true } as any)
          : ({ video: true } as any);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      try {
        stream.getTracks().forEach((t) => t.stop());
      } catch {
        /* no-op */
      }
      return 'granted';
    } catch (error) {
      zoomProviderLogger.error(`[DevicePermissions]: ${name}:`, error);
      return 'denied';
    }
  };

  useAsyncEffect(async () => {
    if (!mediaStream) {
      return;
    }

    const warmUp = async (name: 'camera') => {
      try {
        const res = await requestPermission(name);
        if (res === 'granted') {
          alignPermissionsWithListDevices();
        }
        return res;
      } catch {
        return 'denied';
      }
    };

    const installGestureRetry = (name: 'camera') => {
      const handler = async () => {
        document.removeEventListener('click', handler, true);
        document.removeEventListener('touchend', handler, true);
        await warmUp(name);
      };

      document.addEventListener('click', handler, true);
      document.addEventListener('touchend', handler, true);
    };

    if (
      permissionsState.camera === 'granted' &&
      !didWarmUpCameraRef.current &&
      (mediaStream.getCameraList?.() || []).length === 0
    ) {
      didWarmUpCameraRef.current = true;
      const res = await warmUp('camera');
      if (res !== 'granted') {
        installGestureRetry('camera');
      }
    }
  }, [mediaStream, permissionsState, alignPermissionsWithListDevices]);

  const checkPermissions = async () => {
    zoomProviderLogger.log('checkPermissions called');
    const permissions: Array<'microphone' | 'camera'> = [
      'microphone',
      'camera',
    ];

    const supportsPermissionsAPI =
      typeof navigator !== 'undefined' &&
      !!(navigator as any).permissions &&
      typeof (navigator as any).permissions.query === 'function';

    try {
      if (supportsPermissionsAPI) {
        for (const name of permissions) {
          const permissionStatus = await (navigator as any).permissions.query({
            name,
          });
          zoomProviderLogger.log(
            `[DevicePermissions]: ${name} permission: ${permissionStatus.state}`,
          );
          if (permissionStatus.state !== 'granted') {
            await requestPermission(name);
          }
        }
      } else {
        // iOS/WebKit fallback: directly request to trigger device enumeration
        for (const name of permissions) {
          await requestPermission(name);
        }
      }
    } catch (error) {
      zoomProviderLogger.error('checkPermissions error:', error);
    }

    try {
      const microphonePermissionState = await checkSinglePermission(
        'microphone',
      );
      const cameraPermissionState = await checkSinglePermission('camera');
      setPermissionsState({
        microphone: microphonePermissionState,
        camera: cameraPermissionState,
      });
    } catch (error) {
      zoomProviderLogger.error('Error updating permissions state:', error);
    }
  };

  // Cleanup spatial audio nodes
  const cleanupSpatialAudio = useCallback(() => {
    try {
      if (audioMonitorIntervalRef.current) {
        clearInterval(audioMonitorIntervalRef.current);
        audioMonitorIntervalRef.current = null;
      }

      spatialNodesRef.current.forEach((node) => {
        try {
          node.source.disconnect();
          node.stereoPanner.disconnect();
          node.gainNode.disconnect();
        } catch (error) {
          zoomProviderLogger.error(
            'Error disconnecting spatial audio node during cleanup:',
            error,
          );
        }
      });
      spatialNodesRef.current.clear();

      const audioElements = document.querySelectorAll('audio');
      Array.from(audioElements).forEach((audio) => {
        const originalState = (audio as any)._spatialAudioOriginalState;
        if (originalState) {
          try {
            audio.volume = originalState.volume;
            audio.muted = originalState.muted;
            delete (audio as any)._spatialAudioOriginalState;
          } catch (error) {
            zoomProviderLogger.error(
              'Error resetting original state for audio element during cleanup:',
              error,
            );
            audio.volume = 1.0;
            audio.muted = false;
          }
        } else if (audio.volume === 0) {
          try {
            audio.volume = 1.0;
            audio.muted = false;
          } catch (error) {
            zoomProviderLogger.error(
              'Error resetting volume for audio element during cleanup:',
              error,
            );
          }
        }
      });

      if (
        audioContextRef.current &&
        audioContextRef.current.state !== 'closed'
      ) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    } catch (error) {
      zoomProviderLogger.error('Error during spatial audio cleanup:', error);
    }
  }, []);

  const leaveZoomSession = useCallback(async () => {
    if (!zmClient || !mediaStream || !zmClient.getSessionInfo()?.isInMeeting) {
      return;
    }

    try {
      if (isSharingScreen) {
        await stopScreenSharing();
      }

      if (isSharingToScreen) {
        await stopShareCameraToScreen();
      }

      if (isCameraOn) {
        await mediaStream.stopVideo();
        setIsCameraOn(false);
      }

      setMuted(true);
      await zmClient.leave();

      resetSpatialAudioSettings();

      setIsInConference(false);
      setZoomConferenceName('');
      setIsJoiningConference(false);
      setIsAudioStarted(false);
      isJoiningConferenceRef.current = false;
    } catch (error) {
      zoomProviderLogger.error('Error leaving Zoom session:', error);
      setIsAudioStarted(false);
    }
  }, [
    zmClient,
    mediaStream,
    isSharingScreen,
    isSharingToScreen,
    isCameraOn,
    stopScreenSharing,
    stopShareCameraToScreen,
    cleanupSpatialAudio,
  ]);

  // Monitor and keep Zoom audio elements muted (but functional)
  const startAudioMonitoring = useCallback(() => {
    if (audioMonitorIntervalRef.current) {
      clearInterval(audioMonitorIntervalRef.current);
    }

    audioMonitorIntervalRef.current = setInterval(() => {
      if (spatialAudioDisabled) {
        return;
      }
      if (
        audioContextRef.current &&
        (audioContextRef.current.state === 'suspended' ||
          audioContextRef.current.state === 'closed' ||
          (audioContextRef.current.state as any) === 'interrupted')
      ) {
        // Do not mute original elements while the audio context is not running
        return;
      }
      const audioElements = document.querySelectorAll('audio');

      Array.from(audioElements).forEach((audio) => {
        if (
          audio.id &&
          (audio.id.startsWith('ab-audio-') ||
            audio.id.startsWith('MainAudio-')) &&
          !audio.id.includes('-ue')
        ) {
          // Mute only if we have a spatial node for the corresponding user
          const userIdInId = audio.id
            .replace('ab-audio-', '')
            .replace('MainAudio-', '');
          const candidateKeys = [
            userIdInId,
            String(Number(userIdInId) + 1),
            String(Number(userIdInId) - 1),
          ];
          const hasSpatialForElement = candidateKeys.some((k) =>
            spatialNodesRef.current.has(k),
          );
          if (hasSpatialForElement) {
            if (audio.volume > 0 || !audio.muted) {
              if (!(audio as any)._spatialAudioOriginalState) {
                (audio as any)._spatialAudioOriginalState = {
                  volume: audio.volume,
                  muted: audio.muted,
                };
              }
              audio.volume = 0;
              audio.muted = true;
            }
          }
        }
      });
    }, 1000);
  }, []);

  return {
    micList,
    speakerList,
    cameraList,
    isMicrophonePermissionGranted: permissionsState.microphone === 'granted',
    isCameraPermissionGranted: permissionsState.camera === 'granted',
    handleToggleVideoSharing,
    isCameraOn,
    isCurrentParticipantSpeaking,
    token,
    ue5Token,
    user,
    mediaStream,
    startShareCameraToScreen,
    stopShareCameraToScreen,
    startScreenSharing,
    stopScreenSharing,
    isSharingScreen,
    isSharingScreenModalOpen,
    isSharingToScreen,
    expandedScreenSharing,
    setExpandedScreenSharing,
    expandedCameraSharing,
    setExpandedCameraSharing,
    isJoiningConference,
    isInConference,
    handleJoinToConference,
    conferenceName: zoomConferenceName,
    isLimitedScreenResolution,
    setIsLimitedScreenResolution,
    leaveSession: leaveZoomSession,
    changeParticipantsVolumeLevel,
    resetSpatialAudioSettings,
  };
};

export default useZoom;
